import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';

const RestaurantBooking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previousBookings, setPreviousBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [formData, setFormData] = useState({
    bookingDate: '',
    bookingTime: '',
    partySize: 2,
    specialRequests: ''
  });

  useEffect(() => {
    if (user) {
      fetchPreviousBookings();
    }
  }, [user]);

  const fetchPreviousBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurant_bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPreviousBookings(data || []);
    } catch (error) {
      console.error('Error fetching previous bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage({ type: 'error', text: 'Please login to make a booking' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('restaurant_bookings')
        .insert([{
          user_id: user.id,
          booking_date: formData.bookingDate,
          booking_time: formData.bookingTime,
          party_size: formData.partySize,
          customer_name: user.name,
          customer_email: user.email,
          customer_phone: user.phone || '',
          special_requests: formData.specialRequests,
          status: 'pending'
        }]);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Booking request submitted successfully! We will confirm shortly.' });
      
      // Reset form
      setFormData({
        bookingDate: '',
        bookingTime: '',
        partySize: 2,
        specialRequests: ''
      });

      // Refresh bookings list
      fetchPreviousBookings();

      // Redirect after 3 seconds
      setTimeout(() => navigate('/prive-lounge'), 3000);
      
    } catch (error) {
      console.error('Error creating booking:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to create booking' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#00ff00';
      case 'pending': return '#CDB688';
      case 'cancelled': return '#ff0000';
      default: return '#999';
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <button 
          onClick={() => navigate('/prive-lounge')} 
          className="back-button"
          style={{
            marginBottom: '1rem',
            padding: '0.5rem 1rem',
            background: 'transparent',
            border: '1px solid var(--gold)',
            color: 'var(--gold)',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Back to Privé Lounge
        </button>

        <h1 className="page-title">Restaurant Booking</h1>
        <div className="underline"></div>
        <p className="page-subtitle">Reserve Your Table at Privé Lounge & Bakery</p>

        {message.text && (
          <div className={`message ${message.type}`} style={{
            padding: '1rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            background: message.type === 'success' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
            color: message.type === 'success' ? '#00ff00' : '#ff0000',
            border: `1px solid ${message.type === 'success' ? '#00ff00' : '#ff0000'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Previous Bookings Section */}
        {!loadingBookings && previousBookings.length > 0 && (
          <div className="card" style={{ marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            <h3 style={{ color: '#000', marginBottom: '1rem', fontSize: '1.2rem' }}>Your Previous Bookings</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {previousBookings.map((booking, index) => (
                <div 
                  key={booking.id}
                  style={{
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    background: index === 0 ? 'rgba(205, 182, 136, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderRadius: '8px',
                    border: `1px solid ${index === 0 ? 'rgba(205, 182, 136, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ color: '#000', fontWeight: 'bold' }}>
                      {formatDate(booking.booking_date)} at {booking.booking_time}
                    </div>
                    <div style={{ 
                      color: getStatusColor(booking.status),
                      fontWeight: 'bold',
                      fontSize: '0.85rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      background: `${getStatusColor(booking.status)}22`
                    }}>
                      {getStatusText(booking.status)}
                    </div>
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>
                    Party Size: {booking.party_size} {booking.party_size === 1 ? 'Guest' : 'Guests'}
                  </div>
                  {booking.special_requests && (
                    <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      Note: {booking.special_requests}
                    </div>
                  )}
                  {index === 0 && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: 'var(--gold)',
                      fontWeight: 'bold'
                    }}>
                      Most Recent
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          {/* Display user info (read-only) */}
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            background: 'rgba(205, 182, 136, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(205, 182, 136, 0.3)'
          }}>
            <h3 style={{ color: '#000', marginBottom: '0.75rem', fontSize: '1rem' }}>Booking For:</h3>
            <div style={{ color: '#000', marginBottom: '0.5rem' }}>
              <strong>Name:</strong> {user?.name || 'Not provided'}
            </div>
            <div style={{ color: '#000', marginBottom: '0.5rem' }}>
              <strong>Email:</strong> {user?.email || 'Not provided'}
            </div>
            <div style={{ color: '#000' }}>
              <strong>Phone:</strong> {user?.phone || 'Not provided'}
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Booking Date *
            </label>
            <input
              type="date"
              name="bookingDate"
              value={formData.bookingDate}
              onChange={handleChange}
              min={minDate}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Booking Time *
            </label>
            <select
              name="bookingTime"
              value={formData.bookingTime}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            >
              <option value="">Select a time</option>
              <option value="08:00">8:00 AM</option>
              <option value="08:30">8:30 AM</option>
              <option value="09:00">9:00 AM</option>
              <option value="09:30">9:30 AM</option>
              <option value="10:00">10:00 AM</option>
              <option value="10:30">10:30 AM</option>
              <option value="11:00">11:00 AM</option>
              <option value="11:30">11:30 AM</option>
              <option value="12:00">12:00 PM</option>
              <option value="12:30">12:30 PM</option>
              <option value="13:00">1:00 PM</option>
              <option value="13:30">1:30 PM</option>
              <option value="14:00">2:00 PM</option>
              <option value="14:30">2:30 PM</option>
              <option value="15:00">3:00 PM</option>
              <option value="15:30">3:30 PM</option>
              <option value="16:00">4:00 PM</option>
              <option value="16:30">4:30 PM</option>
              <option value="17:00">5:00 PM</option>
              <option value="17:30">5:30 PM</option>
              <option value="18:00">6:00 PM</option>
              <option value="18:30">6:30 PM</option>
              <option value="19:00">7:00 PM</option>
              <option value="19:30">7:30 PM</option>
              <option value="20:00">8:00 PM</option>
              <option value="20:30">8:30 PM</option>
              <option value="21:00">9:00 PM</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Party Size *
            </label>
            <select
              name="partySize"
              value={formData.partySize}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Special Requests
            </label>
            <textarea
              name="specialRequests"
              value={formData.specialRequests}
              onChange={handleChange}
              placeholder="Any dietary requirements, allergies, or special occasions?"
              rows="4"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000',
                resize: 'vertical'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="cta-button"
            style={{
              width: '100%',
              padding: '1rem',
              background: 'var(--gold)',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? <LoadingSpinner /> : 'Submit Booking Request'}
          </button>
        </form>

        <div className="card" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Opening Hours</h3>
          <p style={{ marginBottom: '0.5rem' }}>Monday - Friday: 8:00 AM - 9:00 PM</p>
          <p style={{ marginBottom: '0.5rem' }}>Saturday - Sunday: 9:00 AM - 10:00 PM</p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
            For same-day bookings or parties larger than 10, please call us directly.
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default RestaurantBooking;
